import React, { useState } from 'react';
import { FiUser, FiUsers, FiPlus, FiX } from 'react-icons/fi';
import { userRequest } from '../../utils/requestMethods';

const Field = ({ label, optional, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
      {label} {optional && <span className="normal-case font-normal text-gray-400">(Optional)</span>}
    </label>
    {children}
  </div>
);

const inputClass = "w-full px-3.5 py-2.5 text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 focus:bg-white placeholder-gray-400 transition-all";
const selectClass = "w-full px-3.5 py-2.5 text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 focus:bg-white transition-all";

const SectionHeader = ({ icon: Icon, title, subtitle, color = "indigo" }) => {
  const colors = {
    indigo: "bg-indigo-50 text-indigo-600 border-indigo-100",
    violet: "bg-violet-50 text-violet-600 border-violet-100",
  };
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border ${colors[color]}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color === 'indigo' ? 'bg-indigo-100' : 'bg-violet-100'}`}>
        <Icon size={15} className={color === 'indigo' ? 'text-indigo-600' : 'text-violet-600'} />
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
  );
};

const AddStudentModal = ({ closeModal, schoolCode, schoolId, classId, goback, onStudentCreated }) => {
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [middlename, setMiddlename] = useState("");
  const [gender, setGender] = useState("");
  const [registrationId, setRegistrationId] = useState("");

  const [parent1Phone, setParent1Phone] = useState("");
  const [parent1Email, setParent1Email] = useState("");
  const [parent1Firstname, setParent1Firstname] = useState("");
  const [parent1Lastname, setParent1Lastname] = useState("");

  const [parent2Phone, setParent2Phone] = useState("");
  const [parent2Email, setParent2Email] = useState("");
  const [parent2Firstname, setParent2Firstname] = useState("");
  const [parent2Lastname, setParent2Lastname] = useState("");
  const [showParent2, setShowParent2] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!firstname || !lastname || !gender) {
      setError("First name, last name and gender are required.");
      setTimeout(() => setError(""), 5000);
      return;
    }
    setLoading(true);
    try {
      const payload = { school_code: schoolCode, class_id: classId, firstname, lastname, gender };

      if (middlename?.trim()) payload.middlename = middlename;
      if (registrationId?.trim()) payload.registration_id = registrationId.trim();

      if (parent1Firstname || parent1Lastname || parent1Phone || parent1Email) {
        payload.parent1 = {};
        if (parent1Firstname?.trim()) payload.parent1.firstname = parent1Firstname;
        if (parent1Lastname?.trim()) payload.parent1.lastname = parent1Lastname;
        if (parent1Phone?.trim()) payload.parent1.phone = parent1Phone;
        if (parent1Email?.trim()) payload.parent1.email = parent1Email;
      }

      if (showParent2 && (parent2Firstname || parent2Lastname || parent2Phone || parent2Email)) {
        payload.parent2 = {};
        if (parent2Firstname?.trim()) payload.parent2.firstname = parent2Firstname;
        if (parent2Lastname?.trim()) payload.parent2.lastname = parent2Lastname;
        if (parent2Phone?.trim()) payload.parent2.phone = parent2Phone;
        if (parent2Email?.trim()) payload.parent2.email = parent2Email;
      }

      await userRequest.post("/children/create", payload);
      onStudentCreated();
      closeModal();
    } catch (err) {
      setError(err.response?.data || "Failed to create student. Please try again.");
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-2 max-h-[72vh] overflow-y-auto pr-1 space-y-5">
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <span className="font-semibold">Error:</span> {error}
        </div>
      )}

      <form onSubmit={submitHandler} className="space-y-5">

        {/* Student Info */}
        <SectionHeader icon={FiUser} title="Student Information" subtitle="Required fields are marked below" />

        <div className="grid grid-cols-3 gap-3">
          <Field label="First Name">
            <input type="text" value={firstname} onChange={(e) => setFirstname(e.target.value)} placeholder="e.g. Amara" className={inputClass} required />
          </Field>
          <Field label="Middle Name" optional>
            <input type="text" value={middlename} onChange={(e) => setMiddlename(e.target.value)} placeholder="e.g. Joy" className={inputClass} />
          </Field>
          <Field label="Last Name">
            <input type="text" value={lastname} onChange={(e) => setLastname(e.target.value)} placeholder="e.g. Okonkwo" className={inputClass} required />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Gender">
            <select value={gender} onChange={(e) => setGender(e.target.value)} className={selectClass} required>
              <option value="">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </Field>
          <Field label="Admission Number" optional>
            <input type="text" value={registrationId} onChange={(e) => setRegistrationId(e.target.value)} placeholder="e.g. ADM/2024/001" className={inputClass} />
          </Field>
        </div>

        <div className="border-t border-gray-100 pt-1" />

        {/* Primary Guardian */}
        <SectionHeader icon={FiUsers} title="Primary Parent / Guardian" subtitle="Optional — used for notifications & invoices" />

        <div className="grid grid-cols-2 gap-3">
          <Field label="First Name" optional>
            <input type="text" value={parent1Firstname} onChange={(e) => setParent1Firstname(e.target.value)} placeholder="Parent first name" className={inputClass} />
          </Field>
          <Field label="Last Name" optional>
            <input type="text" value={parent1Lastname} onChange={(e) => setParent1Lastname(e.target.value)} placeholder="Parent last name" className={inputClass} />
          </Field>
          <Field label="Phone Number" optional>
            <input type="text" value={parent1Phone} onChange={(e) => setParent1Phone(e.target.value)} placeholder="e.g. 08012345678" className={inputClass} />
          </Field>
          <Field label="Email Address" optional>
            <input type="email" value={parent1Email} onChange={(e) => setParent1Email(e.target.value)} placeholder="parent@email.com" className={inputClass} />
          </Field>
        </div>

        {/* Add / Remove Second Parent */}
        {!showParent2 ? (
          <button
            type="button"
            onClick={() => setShowParent2(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100 transition-colors"
          >
            <FiPlus size={14} />
            Add Second Parent / Guardian
          </button>
        ) : (
          <>
            <div className="border-t border-gray-100 pt-1" />
            <div className="flex items-center justify-between">
              <SectionHeader icon={FiUsers} title="Secondary Parent / Guardian" color="violet" />
              <button
                type="button"
                onClick={() => {
                  setShowParent2(false);
                  setParent2Firstname(""); setParent2Lastname("");
                  setParent2Phone(""); setParent2Email("");
                }}
                className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 transition-colors ml-3 flex-shrink-0"
              >
                <FiX size={13} /> Remove
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="First Name" optional>
                <input type="text" value={parent2Firstname} onChange={(e) => setParent2Firstname(e.target.value)} placeholder="Parent first name" className={inputClass} />
              </Field>
              <Field label="Last Name" optional>
                <input type="text" value={parent2Lastname} onChange={(e) => setParent2Lastname(e.target.value)} placeholder="Parent last name" className={inputClass} />
              </Field>
              <Field label="Phone Number" optional>
                <input type="text" value={parent2Phone} onChange={(e) => setParent2Phone(e.target.value)} placeholder="e.g. 08012345678" className={inputClass} />
              </Field>
              <Field label="Email Address" optional>
                <input type="email" value={parent2Email} onChange={(e) => setParent2Email(e.target.value)} placeholder="parent@email.com" className={inputClass} />
              </Field>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={goback}
            className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-xl transition-colors"
          >
            {loading ? "Adding..." : "Add Student"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddStudentModal;
